// The builder's conditions (UI-4): groups joined by ÉS, and inside a group
// rows joined by VAGY — Klaviyo's shape, and exactly the `all` of `any`s the
// engine reads. A part of a stored definition the rows cannot say (a `not`,
// a nested group) is shown as it is, removable, never rewritten.

import { PlusIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ConditionEditor } from "@/pages/segments/ConditionEditor";
import type { BuilderEnv } from "@/pages/segments/data";
import { emptyCondition, newGroup } from "@/pages/segments/model";
import type { BuilderDraft, Catalogue, ConditionDraft, GroupDraft, ItemDraft } from "@/pages/segments/model";

interface Props {
  draft: BuilderDraft;
  catalogue: Catalogue;
  env: BuilderEnv;
  /** Row problems by condition id. */
  errors: Record<string, string>;
  /** After a save attempt: problems are errors, not hints. */
  strict: boolean;
  onChange: (draft: BuilderDraft) => void;
  disabled?: boolean;
}

function Connector({ word }: { word: "ÉS" | "VAGY" }) {
  return (
    <div className="flex items-center gap-3 py-1" aria-hidden="true">
      <span className="h-px flex-1 bg-line" />
      <span
        className={cn(
          "caps-label rounded-control px-2 py-0.5",
          word === "ÉS" ? "bg-surface-2 text-ink" : "text-muted-foreground",
        )}
      >
        {word}
      </span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

export function ConditionsEditor({ draft, catalogue, env, errors, strict, onChange, disabled }: Props) {
  function replace(item: ItemDraft) {
    onChange({ items: draft.items.map((candidate) => (candidate.id === item.id ? item : candidate)) });
  }

  function remove(itemId: string) {
    onChange({ items: draft.items.filter((candidate) => candidate.id !== itemId) });
  }

  function updateCondition(group: GroupDraft, next: ConditionDraft) {
    replace({
      ...group,
      conditions: group.conditions.map((condition) => (condition.id === next.id ? next : condition)),
    });
  }

  function removeCondition(group: GroupDraft, conditionId: string) {
    const conditions = group.conditions.filter((condition) => condition.id !== conditionId);
    if (conditions.length === 0) remove(group.id);
    else replace({ ...group, conditions, wrapped: conditions.length === 1 ? group.wrapped : undefined });
  }

  return (
    <div className="flex flex-col gap-1">
      {draft.items.map((item, index) => (
        <div key={item.id} className="flex flex-col gap-1">
          {index > 0 && <Connector word="ÉS" />}
          {item.kind === "opaque" ? (
            <div className="flex flex-col gap-2 rounded-card border border-dashed border-line-strong bg-surface-2 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-ink">Összetett feltétel</p>
                  <p className="text-label text-muted-foreground">
                    Máshol írták, és itt nem szerkeszthető — a mentés változatlanul megtartja. Törölhető.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  type="button"
                  aria-label="Összetett feltétel törlése"
                  disabled={disabled}
                  onClick={() => remove(item.id)}
                >
                  <XIcon />
                </Button>
              </div>
              <pre className="max-h-48 overflow-auto rounded-control border border-line bg-surface p-2 text-label whitespace-pre-wrap">
                {JSON.stringify(item.node, null, 2)}
              </pre>
            </div>
          ) : (
            <div
              data-slot="condition-group"
              className="flex flex-col gap-1 rounded-card border border-line bg-surface p-3"
            >
              {item.conditions.map((condition, position) => (
                <div key={condition.id} className="flex flex-col gap-1">
                  {position > 0 && <Connector word="VAGY" />}
                  <ConditionEditor
                    condition={condition}
                    catalogue={catalogue}
                    env={env}
                    error={errors[condition.id] ?? null}
                    strict={strict}
                    disabled={disabled}
                    onChange={(next) => updateCondition(item, next)}
                    onRemove={() => removeCondition(item, condition.id)}
                  />
                </div>
              ))}
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  disabled={disabled}
                  onClick={() => replace({ ...item, conditions: [...item.conditions, emptyCondition()] })}
                >
                  <PlusIcon />
                  VAGY feltétel
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
      <div className={cn(draft.items.length > 0 && "pt-2")}>
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={disabled}
          onClick={() => onChange({ items: [...draft.items, newGroup()] })}
        >
          <PlusIcon />
          {draft.items.length === 0 ? "Feltétel hozzáadása" : "ÉS feltétel"}
        </Button>
      </div>
    </div>
  );
}
